import { DotText } from "@/v2-backoffice-client/components/dot"
import { useCustomerBillingQuery } from "@/v2-backoffice-client/customer/billing/customer.billing.query"
import CustomerBillingAccountCard from "@/v2-backoffice-client/customer/billing/CustomerBillingAccountCard"
import CustomerBillingInvoiceTable from "@/v2-backoffice-client/customer/billing/CustomerBillingInvoiceTable"
import CustomerBillingStats from "@/v2-backoffice-client/customer/billing/CustomerBillingStats"
import { deriveOutstandingInvoiceSummary } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"

export type CustomerBillingProps = {
  customerId: number
}

// The customer panel's Billing tab: the Fenerum account this customer bills
// through and the invoice history behind it — both from the hourly
// billing-sync mirror, read-only (Fenerum owns billing; changes are made
// there and appear here within the hour).
function CustomerBilling(props: CustomerBillingProps) {
  const { customerId } = props

  const { data, loading, error, refetch } = useCustomerBillingQuery({
    variables: { customerId },
  })

  const account = data?.getCustomerBillingAccount
  const stats = data?.getCustomerRevenueStats
  // No memoization: stateless component with a primitive prop — it only
  // re-renders when the query result itself changes.
  const invoices = data?.getCustomerInvoices ?? []
  const summary = deriveOutstandingInvoiceSummary(invoices)

  if (!loading && !error && !account) {
    return (
      <p className="text-sm text-muted-foreground">
        No Fenerum billing account is linked to this customer. Expected for
        trials and prospects — for paying customers, the link is made on the
        organisation number and heals on the hourly sync (see the Billing
        Accounts board when it doesn&apos;t).
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {stats && <CustomerBillingStats stats={stats} invoices={invoices} />}

      {account && <CustomerBillingAccountCard account={account} />}

      <section className="flex min-h-0 flex-col gap-2">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold">Invoices</h3>
          {summary.outstandingCount > 0 && (
            <DotText
              variant={summary.overdueCount > 0 ? "destructive" : "info"}
              className="text-xs"
            >
              {summary.outstandingByCurrency.join(" + ")} outstanding
              {summary.overdueCount > 0 && ` (${summary.overdueCount} overdue)`}
            </DotText>
          )}
        </div>

        <CustomerBillingInvoiceTable
          invoices={invoices}
          isLoading={loading}
          error={error}
          onRefresh={() => {
            void refetch()
          }}
        />
      </section>
    </div>
  )
}

export default CustomerBilling
